import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { relicById } from '../game/relics';
import { classById } from '../game/classes';
import { ACHIEVEMENTS } from '../game/achievements';
import CurseSelect from './CurseSelect';
import CombatHistory from '../components/CombatHistory';
import HighlightReel from '../components/HighlightReel';
import { RelicBadge, BlessingBadge } from '../components/RelicBadge';
import { BLESSINGS } from '../game/blessings';
import { Copy, Check, Trophy, RotateCw, Coins, Skull, Crown, Award } from 'lucide-react';

export default function GameOver() {
  const { lastRunSummary, playerName, setPlayerName, goToMenu, setScreen, runAchievements, submitFinalScore, bestStreak, ghostRun } = useGame();
  const [submitted, setSubmitted] = useState(null);
  const [tokensAwarded, setTokensAwarded] = useState(0);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nameInput, setNameInput] = useState(playerName || '');

  if (!lastRunSummary) return null;
  const { victory, bossDefeated, floor, score, seed, player, ascension, curse, streak, streakBonus, streakLost } = lastRunSummary;
  const cls = player.classId ? classById(player.classId) : null;

  const submit = async () => {
    const name = (nameInput || playerName || 'ANON_GOON').toUpperCase();
    setBusy(true);
    const res = await submitFinalScore({ name });
    if (res?.created) {
      setSubmitted(res.created);
      setTokensAwarded(res.tokens);
    }
    setBusy(false);
  };

  const shareCode = submitted?.run_code || seed;

  const copy = () => {
    navigator.clipboard?.writeText(shareCode).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-6 py-8">
      <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">RUN OVER</div>
      <h1 className={`font-display text-6xl sm:text-8xl font-black uppercase mt-2 ${victory ? 'text-[color:var(--neon)]' : 'text-[color:var(--red)]'}`}
        data-testid="gameover-title">
        {victory ? 'W. GG.' : 'YOU DIED'}
      </h1>

      <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">
        {cls && <span className="brutal-border-dim px-2 py-1 text-xs font-bold uppercase" style={{color: cls.color, borderColor: cls.color}}>{cls.icon} {cls.name}</span>}
        <span className="brutal-border-dim px-2 py-1 text-xs font-bold uppercase text-[color:var(--pink)]" data-testid="ascension-badge">A{ascension || 0}</span>
        {curse && <span className="brutal-border-pink px-2 py-1 text-xs font-bold uppercase text-[color:var(--red)]" data-testid="run-was-cursed"><Skull size={12} className="inline"/> CURSED</span>}
        {victory && streak > 0 && (
          <span className="streak-badge" data-testid="streak-badge">
            <Crown size={12}/> STREAK × {streak}
          </span>
        )}
        {!victory && streakLost > 1 && (
          <span className="brutal-border-pink px-2 py-1 text-xs font-bold uppercase text-[color:var(--red)]" data-testid="streak-broken">
            <Skull size={12} className="inline"/> {streakLost}-STREAK BROKEN
          </span>
        )}
        {bestStreak > 0 && (
          <span className="brutal-border-dim px-2 py-1 text-xs font-bold uppercase text-[color:var(--neon)]" data-testid="best-streak-badge">
            BEST × {bestStreak}
          </span>
        )}
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl w-full">
        <StatBox label="FLOOR" value={floor} testid="stat-floor" />
        <StatBox label="SCORE" value={score} testid="stat-score" />
        <StatBox label="GOLD" value={player.gold} testid="stat-gold" />
        <StatBox label="DECK" value={player.deck.length} testid="stat-deck" />
      </div>

      {/* v3.14 GHOST comparison — did you beat your previous best on this seed? */}
      {(() => {
        // Use ghostRun snapshot loaded at run START so it's not overwritten by the current run's save.
        const g = ghostRun;
        if (!g) return null;
        const beatScore = score > g.score;
        const beatFloor = floor > g.floor;
        const newBest = beatScore || (score === g.score && beatFloor);
        return (
          <div
            className="mt-6 max-w-4xl w-full brutal-border p-4"
            style={{
              borderColor: newBest ? '#39ff14' : '#a0a0a0',
              boxShadow: newBest ? '4px 4px 0 #39ff14' : '3px 3px 0 #a0a0a0',
              background: newBest ? '#061a06' : '#0a0a0a',
            }}
            data-testid="ghost-compare-panel"
          >
            <div className="text-xs uppercase tracking-widest mb-2 font-mono" style={{ color: newBest ? '#39ff14' : '#a0a0a0' }}>
              {newBest ? '★ NEW BEST ON THIS SEED ★' : 'GHOST · YOUR PREVIOUS BEST'}
            </div>
            <div className="flex items-center justify-around text-sm uppercase tracking-widest font-mono">
              <div>
                <div className="text-[10px] text-[color:var(--text-dim)]">GHOST FLOOR</div>
                <div className="font-display font-black text-2xl" data-testid="ghost-compare-floor">{g.floor}</div>
              </div>
              <div>
                <div className="text-[10px] text-[color:var(--text-dim)]">GHOST SCORE</div>
                <div className="font-display font-black text-2xl" data-testid="ghost-compare-score">{g.score}</div>
              </div>
              <div>
                <div className="text-[10px] text-[color:var(--text-dim)]">Δ FLOOR</div>
                <div className="font-display font-black text-2xl" style={{ color: beatFloor ? '#39ff14' : (floor === g.floor ? '#ffd700' : '#ff2b2b') }}>
                  {floor > g.floor ? `+${floor - g.floor}` : floor === g.floor ? '0' : `-${g.floor - floor}`}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-[color:var(--text-dim)]">Δ SCORE</div>
                <div className="font-display font-black text-2xl" style={{ color: beatScore ? '#39ff14' : (score === g.score ? '#ffd700' : '#ff2b2b') }}>
                  {score > g.score ? `+${score - g.score}` : score === g.score ? '0' : `-${g.score - score}`}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Achievements earned this run */}
      {runAchievements.length > 0 && (
        <div className="mt-6 max-w-4xl w-full brutal-border p-4 bg-[#0a1a0a]" data-testid="run-achievements-panel">
          <div className="text-xs uppercase tracking-widest text-[color:var(--neon)] mb-2 flex items-center gap-2">
            <Award size={14}/> ACHIEVEMENTS THIS RUN
          </div>
          <div className="flex flex-wrap gap-2">
            {runAchievements.map(aid => {
              const a = ACHIEVEMENTS[aid];
              if (!a) return null;
              return (
                <div key={aid} className="brutal-border-dim px-2 py-1 text-xs">
                  <span className="font-display font-black uppercase text-[color:var(--neon)]">{a.name}</span>
                  <span className="text-[color:var(--pink)] ml-2">+{a.tokens}🪙</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-2 flex-wrap max-w-3xl justify-center">
        {player.relics.map(rid => (
          <RelicBadge
            key={rid}
            relicId={rid}
            pickupFloor={lastRunSummary?.relicPickupFloors?.[rid]}
            triggers={lastRunSummary?.relicTriggers?.[rid]}
            testid={`gameover-relic-${rid}`}
          />
        ))}
        {(player._blessings || []).map(bid => (
          <BlessingBadge
            key={bid}
            blessingId={bid}
            pickupFloor={lastRunSummary?.blessingPickupFloors?.[bid]}
            testid={`gameover-blessing-${bid}`}
          />
        ))}
      </div>

      {!submitted ? (
        <div className="mt-10 max-w-md w-full">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2">TAG</div>
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value.slice(0, 16).toUpperCase())}
            placeholder="ANON_GOON"
            className="w-full bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--neon)] px-3 py-2 text-lg font-mono uppercase outline-none"
            data-testid="submit-name-input"
          />
          <button
            className="brutal-btn w-full mt-4 text-lg"
            onClick={submit}
            disabled={busy}
            data-testid="submit-score-button"
          >
            {busy ? 'SUBMITTING...' : 'SUBMIT SCORE + EARN SLOP TOKENS'}
          </button>
        </div>
      ) : (
        <div className="mt-10 w-full max-w-2xl">
          <div className="brutal-border-pink p-6 bg-[#0a0a0a]">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">RUN CODE — share with friends</div>
            <div className="mt-2 flex items-center justify-between">
              <div className="font-display text-5xl font-black text-[color:var(--pink)] tracking-widest" data-testid="share-run-code">{shareCode}</div>
              <button className="brutal-btn text-xs px-3 py-2" onClick={copy} data-testid="copy-run-code-button">
                {copied ? <Check size={16}/> : <Copy size={16} />}
              </button>
            </div>
          </div>
          <div className="mt-4 brutal-border p-4 bg-[#0a1a0a] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Coins className="text-[color:var(--neon)]"/>
              <span className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">SLOP TOKENS EARNED</span>
            </div>
            <div className="font-display text-4xl font-black text-[color:var(--neon)]" data-testid="tokens-earned">+{tokensAwarded}</div>
          </div>
          {victory && streakBonus > 0 && (
            <div className="mt-2 brutal-border-pink p-2 bg-[#0a0010] flex items-center justify-between" data-testid="streak-bonus-row">
              <span className="text-[10px] uppercase tracking-widest text-[color:var(--pink)]"><Crown size={10} className="inline"/> STREAK × {streak} BONUS</span>
              <span className="font-display text-xl font-black text-[color:var(--pink)]">+{streakBonus}🪙</span>
            </div>
          )}
          {/* Curse this run */}
          <div className="mt-4">
            <CurseSelect runCode={shareCode} />
          </div>
        </div>
      )}

      <div className="mt-8 flex gap-4">
        <button className="brutal-btn" onClick={() => setScreen('leaderboard')} data-testid="view-leaderboard-btn">
          <Trophy size={16} /> LEADERBOARD
        </button>
        <button className="brutal-btn-pink brutal-btn" onClick={goToMenu} data-testid="back-to-menu-button">
          <RotateCw size={16} /> BACK TO MENU
        </button>
      </div>

      {/* v3.0: POST-RUN HIGHLIGHT REEL — shareable stat card */}
      <HighlightReel summary={lastRunSummary} />

      {/* v1.0: Full Combat History timeline embedded */}
      <div className="mt-10 w-full">
        <CombatHistory summary={lastRunSummary} />
      </div>
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="brutal-border p-4 bg-[#0a0a0a] brutal-shadow">
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">{label}</div>
      <div className="font-display text-4xl font-black text-[color:var(--neon)] mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
